import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

const AdminProductDialog = ({ open, onClose, product, onSuccess, company }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: 0,
        initialQuantity: '',
        entireGame: null,
        games: [],
        photo: null,
    });
    const [games, setGames] = useState([]);
    const [error, setError] = useState(null);
    const [gameSelection, setGameSelection] = useState('Todos');

    useEffect(() => {
        if (product) {
            setFormData({
                name: product.name || '',
                description: product.description || '',
                price: product.price || 0,
                initialQuantity: product.quantity || '',
                games: Array.isArray(product.games)
                    ? product.games.map((game) => game.id)
                    : [],
                photo: null,
            });
            setGameSelection(product.entireGame ? 'Todos' : 'Alguns');
        }
    }, [product]);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const gamesData =
                    await RequestService.getGamesAsSuperAdmin(company);
                setGames(Array.isArray(gamesData) ? gamesData : []);
            } catch (err) {
                console.error('Erro ao carregar jogos:', err);
                setGames([]);
            }
        };

        fetchGames();
    }, [company]);

    const handleGameSelection = (event, newSelection) => {
        if (newSelection) {
            setGameSelection(newSelection);
            setFormData((prev) => ({
                ...prev,
                entireGame: newSelection === 'Todos',
                games: newSelection === 'Todos' ? [] : prev.games,
            }));
        }
    };

    const handleGameToggle = (gameId) => {
        setFormData((prev) => {
            const isSelected = prev.games.includes(gameId);
            const newGames = isSelected
                ? prev.games.filter((id) => id !== gameId)
                : [...prev.games, gameId];
            return { ...prev, games: newGames };
        });
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files[0] : value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('price', formData.price);
            formDataToSend.append('initialQuantity', formData.initialQuantity);

            if (gameSelection === 'Todos') {
                formDataToSend.append('entireGame', true);
            } else {
                formDataToSend.append('entireGame', false);
                formData.games.forEach((gameId) =>
                    formDataToSend.append('gameIds', gameId)
                );
            }

            if (formData.photo instanceof File) {
                formDataToSend.append('photo', formData.photo);
            }

            await RequestService.updateProductAsSuperAdmin(
                product.productId,
                formDataToSend
            );

            setFormData({
                name: '',
                description: '',
                price: 0,
                initialQuantity: '',
                games: [],
                photo: null,
                notify: false,
            });

            onSuccess('Produto atualizado com sucesso');
            onClose();
        } catch (err) {
            setError('Erro ao atualizar o produto. Verifique os dados.');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Editar Produto
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: '#F5F5F5',
                            padding: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                border: '1px dashed #CCCCCC',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                overflow: 'hidden',
                            }}
                        >
                            {formData.photo ? (
                                typeof formData.photo === 'string' ? (
                                    <img
                                        src={product.photoUrl}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={URL.createObjectURL(
                                            formData.photo
                                        )}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                )
                            ) : (
                                <AddPhotoAlternateOutlinedIcon
                                    fontSize="large"
                                    color="disabled"
                                />
                            )}
                        </Box>
                        <Button variant="contained" component="label" fullWidth>
                            Selecionar Imagem
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                name="photo"
                                onChange={handleChange}
                            />
                        </Button>
                    </Box>

                    <TextField
                        label="Título"
                        name="name"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                        fullWidth
                    />
                    <TextField
                        label="Descrição"
                        name="description"
                        value={formData.description}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                description: e.target.value,
                            })
                        }
                        fullWidth
                        multiline
                        rows={3}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Custo"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    price: e.target.value,
                                })
                            }
                            fullWidth
                        />
                        <TextField
                            label="Quantidade"
                            name="initialQuantity"
                            type="number"
                            value={formData.initialQuantity}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    initialQuantity: e.target.value,
                                })
                            }
                            fullWidth
                        />
                    </Box>

                    {/* Game Selection Section */}
                    <Typography>Jogos</Typography>
                    <ToggleButtonGroup
                        value={gameSelection}
                        exclusive
                        onChange={handleGameSelection}
                        fullWidth
                    >
                        <ToggleButton value="Todos">Todos</ToggleButton>
                        <ToggleButton value="Alguns">Alguns</ToggleButton>
                    </ToggleButtonGroup>
                    {gameSelection === 'Alguns' && (
                        <List
                            sx={{
                                border: '1px solid #E0E0E0',
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            {games.map((game) => (
                                <ListItem
                                    key={game.id}
                                    disablePadding
                                    sx={{
                                        borderBottom: '1px solid #E0E0E0',
                                        '&:last-child': {
                                            borderBottom: 'none',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#EDF8FF',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#F5F5F5',
                                        },
                                    }}
                                    className={
                                        formData.games.includes(game.id)
                                            ? 'Mui-selected'
                                            : ''
                                    }
                                    onClick={() => handleGameToggle(game.id)}
                                >
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            checked={formData.games.includes(
                                                game.id
                                            )}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={game.name} />
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                >
                    Salvar Alterações
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminProductDialog;
